/**
 * Clerk-based AuthProvider implementation.
 *
 * Wraps the Clerk React SDK hooks and maps them to the shared
 * AuthContextType interface.
 */

import React, { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth as useClerkAuth, useUser, useClerk } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { ImpersonationManager } from '../../utils/impersonationManager';
import { useImpersonation } from './useImpersonation';
import { User, AuthContextType } from './types';
import { fetchDatabaseAdminFlag } from './backendAuthorization';
import PostHogService from '../../posthog';

interface Props {
  children: ReactNode;
  context: React.Context<AuthContextType | undefined>;
}

export const ClerkAuthProvider: React.FC<Props> = ({ children, context }) => {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const clerk = useClerk();

  const [user, setUser] = useState<User | null>(null);

  // Sync Clerk user to local state
  useEffect(() => {
    let isComponentMounted = true;

    const syncClerkUser = async () => {
      if (!isLoaded || !isSignedIn || !clerkUser) {
        return;
      }

      const primaryEmail = clerkUser.primaryEmailAddress?.emailAddress || '';
      const baseUser: User = {
        id: clerkUser.id,
        email: primaryEmail,
        name: clerkUser.fullName || clerkUser.firstName || '',
        username: clerkUser.username || primaryEmail.split('@')[0],
        is_admin: false,
        picture: clerkUser.imageUrl,
      };

      // Set identity immediately, then hydrate authorization from backend.
      if (isComponentMounted) {
        setUser(baseUser);
        Sentry.setUser({ id: clerkUser.id, email: primaryEmail });
      }

      const backendAccessToken = await getToken({ template: 'backend' });
      const databaseAdminFlag = backendAccessToken
        ? await fetchDatabaseAdminFlag(backendAccessToken)
        : null;

      if (isComponentMounted) {
        setUser({
          ...baseUser,
          is_admin: databaseAdminFlag ?? false,
        });
      }
    };

    if (isLoaded && isSignedIn && clerkUser) {
      void syncClerkUser();
    } else if (isLoaded && !isSignedIn) {
      setUser(null);
      Sentry.setUser(null);
      ImpersonationManager.clearSession();
    }

    return () => {
      isComponentMounted = false;
    };
  }, [isLoaded, isSignedIn, clerkUser, getToken]);

  const isAdmin = user?.is_admin || false;
  const {
    impersonationContext,
    startImpersonation,
    stopImpersonation,
    getImpersonationTimeRemaining,
  } = useImpersonation(user, isAdmin);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      return await getToken({ template: 'backend' });
    } catch (err) {
      console.error('Error getting access token:', err);
      return null;
    }
  }, [getToken]);

  const logout = useCallback(async () => {
    if (impersonationContext.isImpersonating) {
      ImpersonationManager.clearSession();
    }
    await clerk.signOut();
    setUser(null);
    Sentry.setUser(null);
    PostHogService.reset();
  }, [clerk, impersonationContext]);

  const checkAuth = useCallback(async () => isLoaded && isSignedIn, [isLoaded, isSignedIn]);

  const isAuthenticated = isLoaded && isSignedIn;
  const displayName = user?.name || user?.username || user?.email || 'User';

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading: !isLoaded,
      displayName,
      logout,
      checkAuth,
      getAccessToken,
      isAdmin,
      impersonationContext,
      startImpersonation,
      stopImpersonation,
      getImpersonationTimeRemaining,
    }),
    [
      user, isAuthenticated, isLoaded, displayName,
      logout, checkAuth, getAccessToken, isAdmin,
      impersonationContext, startImpersonation,
      stopImpersonation, getImpersonationTimeRemaining,
    ],
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};
